import React, { useCallback, useEffect, useState } from 'react';
import { supabaseClient } from '../../config/supabase';
import { requestAffiliateTripLink } from '../../lib/affiliateTripLinks';
import HoverHelp from '../shared/HoverHelp';

const AFFILIATE_STATUS = {
    DER: { label: 'Driver En Route', tone: 'bg-blue-100 text-blue-800 border-blue-200' },
    DOS: { label: 'Driver On Site', tone: 'bg-amber-100 text-amber-800 border-amber-200' },
    POB: { label: 'Passenger On Board', tone: 'bg-purple-100 text-purple-800 border-purple-200' },
    PDO: { label: 'Passenger Drop-Off', tone: 'bg-green-100 text-green-800 border-green-200' },
};

function AffiliateTripLinkPanel({ assignment, theme = 'light', userId }) {
    const dark = theme === 'dark';
    const [link, setLink] = useState(null);
    const [loading, setLoading] = useState(false);
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState('');
    const [copied, setCopied] = useState(false);

    const assignmentId = assignment?.id;

    const loadExistingLink = useCallback(async () => {
        if (!assignmentId) return;
        setLoading(true);
        setError('');
        try {
            const nextLink = await requestAffiliateTripLink(assignmentId, 'GET');
            setLink(nextLink);
        } catch (err) {
            if (err.status !== 404) {
                setError(err.message || 'Could not load affiliate link.');
            }
            setLink(null);
        } finally {
            setLoading(false);
        }
    }, [assignmentId]);

    useEffect(() => {
        loadExistingLink();
    }, [loadExistingLink]);

    useEffect(() => {
        if (!link?.id || typeof supabaseClient.channel !== 'function') return;

        const channel = supabaseClient.channel(`affiliate-trip-status-${link.id}`, {
            config: { broadcast: { self: false } },
        });

        if (!channel || typeof channel.on !== 'function') return;

        channel
            .on('postgres_changes', {
                event: 'INSERT',
                schema: 'public',
                table: 'affiliate_trip_status_events',
                filter: `affiliate_trip_link_id=eq.${link.id}`,
            }, (payload) => {
                const status = payload?.new?.status;
                if (!status) return;
                setLink((current) => current ? { ...current, status } : current);
            })
            .subscribe();

        return () => {
            if (typeof supabaseClient.removeChannel === 'function') {
                supabaseClient.removeChannel(channel);
            }
        };
    }, [link?.id]);

    const handleMintOrFetch = async () => {
        if (!assignmentId) return;
        setCreating(true);
        setError('');
        try {
            const nextLink = await requestAffiliateTripLink(assignmentId, 'POST');
            setLink(nextLink);
        } catch (err) {
            setError(err.message || 'Could not create affiliate link.');
        } finally {
            setCreating(false);
        }
    };

    const handleCopy = async () => {
        if (!link?.url) return;
        setCopied(false);
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(link.url);
            } else {
                const textarea = document.createElement('textarea');
                textarea.value = link.url;
                textarea.setAttribute('readonly', '');
                textarea.style.position = 'absolute';
                textarea.style.left = '-9999px';
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand('copy');
                textarea.remove();
            }
            setCopied(true);
        } catch (err) {
            setError('Could not copy link. Select and copy it manually.');
        }
    };

    const statusMeta = AFFILIATE_STATUS[link?.status] || {
        label: link?.status || 'No status yet',
        tone: dark ? 'bg-gray-700 text-gray-200 border-gray-600' : 'bg-gray-100 text-gray-700 border-gray-200',
    };

    const labelCls = dark ? 'text-gray-400' : 'text-gray-500';
    const valueCls = dark ? 'text-gray-100' : 'text-gray-800';
    const inputCls = dark
        ? 'bg-gray-700 border-gray-600 text-gray-100'
        : 'bg-white border-gray-300 text-gray-800';

    return (
        <div data-m7-touch className={`mt-4 pt-4 border-t ${dark ? 'border-gray-700' : 'border-gray-100'}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <p className={`flex items-center gap-1 text-xs font-semibold uppercase tracking-wide ${labelCls}`}>
                        <span>Affiliate Trip Status Link</span>
                        <HoverHelp helpId="dispatch.affiliate-trip-link" userId={userId} />
                    </p>
                    <p className={`text-xs mt-1 ${labelCls}`}>
                        Share this no-login link with the affiliate driver so they can tap DER / DOS / POB / PDO.
                    </p>
                </div>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${statusMeta.tone}`}>
                    {statusMeta.label}
                </span>
            </div>

            <div className="mt-3 flex flex-col md:flex-row gap-2">
                <input
                    type="text"
                    readOnly
                    value={link?.url || ''}
                    placeholder={loading ? 'Checking for existing link…' : 'No affiliate link minted yet'}
                    className={`flex-1 px-3 py-2 border rounded-lg text-sm ${inputCls}`}
                    aria-label="Affiliate trip link URL"
                />
                <button
                    type="button"
                    onClick={handleMintOrFetch}
                    disabled={creating || loading}
                    className="px-3 py-2 text-xs font-medium rounded-lg bg-[var(--btn-fill)] text-[var(--btn-ink)] hover:bg-[var(--btn-fill-hover)] disabled:opacity-50"
                >
                    {creating ? 'Working…' : link ? 'Refresh Link' : 'Mint Link'}
                </button>
                <button
                    type="button"
                    onClick={handleCopy}
                    disabled={!link?.url}
                    className={`px-3 py-2 text-xs font-medium rounded-lg border disabled:opacity-50 ${dark ? 'border-gray-600 text-gray-200 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                >
                    {copied ? 'Copied' : 'Copy Link'}
                </button>
            </div>

            {link?.expiresAt && (
                <p className={`text-[11px] mt-2 ${labelCls}`}>
                    Expires {new Date(link.expiresAt).toLocaleString()}
                </p>
            )}
            {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
        </div>
    );
}

export default AffiliateTripLinkPanel;
